import React from 'react';
import { useQuery } from '@apollo/client';
import { Button, getIcon } from '@quality24/design-system';
import { UUIDv4 } from '@quality24/inpatient-typings';

import ActionCard from '@/inpatient-patient-pwa/molecules/ActionCard';
import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';
import { parseGroups } from '@/inpatient-patient-pwa/pages/OrderTriggerPage/utils';
import {
  ComposedPatientCallReason,
  QueryResult,
} from '@/inpatient-patient-pwa/pages/OrderTriggerPage/types';

import {
  FETCH_GROUP_ITEM_BY_SERVICE_QUERY,
  FETCH_GROUP_ITEM_QUERY,
} from './graphql';
import useOrderFlow from './useOrderFlow';
import OrderFlowContainer from './OrderFlowContainer';
import useReasonSelection from './useReasonSelection';
import { canBeTriggered } from './utils';

export interface Props {
  /**
   * Pre-resolved reason — used by the OrderTriggerPage listing, which already
   * fetched the whole group tree, to avoid N+1 queries. Takes precedence over
   * `serviceGroupItemId`/`serviceId`.
   */
  reason?: ComposedPatientCallReason;
  /** A specific `service_group_item` to open (self-fetches). */
  serviceGroupItemId?: UUIDv4;
  /** A single service to open (HospitalInformationPage `service_button`). */
  serviceId?: UUIDv4;

  /** Presentation */
  label: string;
  icon: string;
  variant?: 'card' | 'inline';
  disabled?: boolean;

  /** Called after a successful order creation. */
  onTriggered?: () => void;

  className?: string;
}

/**
 * Self-contained button that opens the patient-call order flow for a single
 * `service_group_item` (or `service`). Encapsulates the trigger state machine
 * and modal chain so it can be reused in the OrderTriggerPage listing and
 * inside the HospitalInformationPage. When `reason` is provided the data is
 * reused as-is; otherwise it self-fetches by id.
 */
const OrderButton: React.FunctionComponent<Props> = ({
  reason: providedReason,
  serviceGroupItemId,
  serviceId,
  label,
  icon,
  variant = 'card',
  disabled = false,
  onTriggered,
  className,
}) => {
  const {
    user: { admissionId },
  } = useAuth();
  const selectReason = useReasonSelection();

  const { data, loading } = useQuery<QueryResult>(
    serviceGroupItemId
      ? FETCH_GROUP_ITEM_QUERY
      : FETCH_GROUP_ITEM_BY_SERVICE_QUERY,
    {
      variables: serviceGroupItemId
        ? { id: serviceGroupItemId, admissionId }
        : { serviceId, admissionId },
      skip:
        !!providedReason || !admissionId || (!serviceGroupItemId && !serviceId),
      fetchPolicy: 'cache-and-network',
    },
  );

  const reason = React.useMemo(
    () => providedReason ?? parseGroups(data?.groups?.nodes)[0],
    [providedReason, data?.groups?.nodes],
  );

  const flow = useOrderFlow({ admissionId, onTriggered });
  const { dispatch } = flow;

  const handleClick = React.useCallback(() => {
    if (!reason) return;
    const resolved = selectReason(reason);
    if (!resolved) return;
    dispatch({ type: 'init', reason: resolved });
  }, [reason, selectReason, dispatch]);

  // Graceful handling of broken references (jsonb has no FK): hide + warn.
  if (!providedReason && !loading && data && !reason) {
    // eslint-disable-next-line no-console
    console.warn(
      `OrderButton: no enabled group item found for ${
        serviceGroupItemId
          ? `serviceGroupItemId=${serviceGroupItemId}`
          : `serviceId=${serviceId}`
      }`,
    );
    return null;
  }

  if (!reason) return null;

  // A reason with an active alert is "in progress" and can't be re-triggered.
  const inProgress = !canBeTriggered(reason);

  // The order trigger `submitting` state is global (shared store), so every
  // OrderButton disables together while a mutation is in flight. The page-level
  // SpinnerOverlay (rendered by the container) provides the loading feedback.
  const isDisabled = disabled || inProgress || flow.operation.submitting;

  return (
    <>
      {variant === 'inline' ? (
        <Button
          className={className}
          size="lg"
          icon={getIcon(icon) || undefined}
          onClick={handleClick}
          disabled={isDisabled}
          fluid
        >
          {label}
        </Button>
      ) : (
        <ActionCard
          className={className}
          title={label}
          icon={icon}
          disabled={isDisabled}
          inProgress={inProgress}
          onSelect={handleClick}
        />
      )}

      <OrderFlowContainer admissionId={admissionId} flow={flow} />
    </>
  );
};

export default OrderButton;
